import React from 'react';
import { Link } from 'react-router-dom';
import { Handshake, ArrowRight, Star, MapPin, CheckCircle, MessageCircle, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { generateWhatsAppLink, trackWhatsAppClick } from '@/utils/whatsappTemplates';
import { getFeaturedPartners, getUniversityPartners } from '@/data/partnerConfig';
import { CONTACT_INFO } from '@/data/mockData';

const Partners = () => {
  const partners = getFeaturedPartners();
  const universities = getUniversityPartners();

  const handleWhatsAppClick = () => {
    trackWhatsAppClick({ templateName: 'partnership', page: 'partners' });
    const message = 'Hi! I\'m interested in becoming a Rocket Van Express delivery partner.';
    const link = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(link, '_blank');
  };

  const benefits = [
    { title: 'Co-branded Landing Page', description: 'Custom page for your customers' },
    { title: 'Exclusive Discount Codes', description: 'Special rates for your clients' },
    { title: 'Priority Service', description: 'Dedicated support line' },
    { title: 'Monthly Reporting', description: 'Delivery analytics dashboard' }
  ];

  return (
    <div className="min-h-screen" data-testid="partners-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Handshake className="h-16 w-16 text-[#039fff] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Partner With Rocket Van Express</h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join our network of trusted partners and offer your customers reliable delivery services with exclusive benefits.
            </p>
            <Button 
              size="lg" 
              className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8"
              onClick={handleWhatsAppClick}
              data-testid="become-partner-btn"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Become a Partner
            </Button>
          </div>
        </div>
      </section>

      {/* University Partners */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <GraduationCap className="h-8 w-8 text-[#039fff]" />
              <h2 className="text-3xl font-bold text-[#001A47]">Student Moving Partners</h2>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Special rates for students at Western Cape universities. Moving in or out of res? We've got you covered.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {universities.map((uni) => (
              <Link key={uni.slug} to={`/partners/${uni.slug}`}>
                <Card className="h-full hover:shadow-lg transition-all border-2 border-transparent hover:border-[#039fff] group">
                  <CardContent className="p-6 text-center">
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform group-hover:scale-110"
                      style={{ backgroundColor: `${uni.primaryColor}15` }}
                    >
                      <GraduationCap className="h-8 w-8" style={{ color: uni.primaryColor }} />
                    </div>
                    <h3 className="font-bold text-lg text-[#001A47] mb-1">{uni.shortName || uni.name}</h3>
                    <p className="text-sm text-gray-500 mb-3">{uni.name}</p>
                    {uni.discount && (
                      <Badge className="bg-[#fb6402] text-white mb-3">
                        <Star className="h-3 w-3 mr-1" />
                        {uni.discount}
                      </Badge>
                    )}
                    <p className="text-xs text-gray-600 mb-3">
                      Code: <span className="font-mono font-bold">{uni.discountCode}</span>
                    </p>
                    <span className="text-[#039fff] text-sm font-medium flex items-center justify-center">
                      View Details <ArrowRight className="h-4 w-4 ml-1" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#001A47] mb-4">Partnership Benefits</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              When you partner with Rocket Van Express, you get more than just delivery services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-[#039fff]/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-6 w-6 text-[#039fff]" />
                  </div>
                  <h3 className="font-semibold text-[#001A47] mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Partners */}
      {partners.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#001A47] mb-4">Business Partners</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Trusted businesses that rely on Rocket Van Express for their delivery needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {partners.map((partner) => (
                <Link key={partner.slug} to={`/partners/${partner.slug}`}>
                  <Card className="h-full hover:shadow-lg transition-all border-2 border-transparent hover:border-[#039fff]">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-bold text-lg text-[#001A47]">{partner.name}</h3>
                          <Badge variant="outline" className="mt-1">{partner.tagline}</Badge>
                        </div>
                        {partner.discount && (
                          <Badge className="bg-[#fb6402] text-white">
                            <Star className="h-3 w-3 mr-1" />
                            {partner.discount}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {partner.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin className="h-4 w-4 mr-1" />
                          {partner.areas.slice(0, 2).join(', ')}
                          {partner.areas.length > 2 && ` +${partner.areas.length - 2}`}
                        </div>
                        <span className="text-[#039fff] text-sm font-medium flex items-center">
                          View Page <ArrowRight className="h-4 w-4 ml-1" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-[#001A47] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Partner With Us?</h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            Let's discuss how we can help your customers with reliable, trackable deliveries.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#fb6402] hover:bg-[#e55a02]"
              onClick={handleWhatsAppClick}
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              WhatsApp Us
            </Button>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#001A47]">
                Contact Form
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;
